import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgSelectModule } from '@ng-select/ng-select';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_BASE_HREF, CommonModule } from '@angular/common';

import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { AuthGuardCAS } from './guards/auth.guard.cas';
import { AuthGuardKeycloak } from './guards/auth.guard.keycloak';
import { SESSION_STORAGE_KEYS } from './constants/storage-keys';

// services
import { AuthService } from './services/auth/auth-service';
import { environment } from '../environments/environment';
import { PageNameComponent } from './components/menu/page-name/page-name.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ModalModule } from 'ngx-bootstrap/modal';
import { ToastrModule } from 'ngx-toastr';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDropdownDirective, BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { FormioAppConfig, FormioModule } from '@formio/angular';
import { AppConfig } from './formio-config';
import { ErrorInterceptor } from './error.interceptor';
import { SafePipe } from './safe.pipe';
import { HeaderComponent } from './components/header/header.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { DashboardTabsComponent } from './components/dashboard/dashboard-tabs/dashboard-tabs.component';
import { FileListComponent } from './components/dashboard/file-list/file-list.component';
import { DocumentPreviewComponent } from './components/document-preview/document-preview.component';
import { LoginComponent } from './components/menu/login/login.component';
import { LanguageMenuComponent } from './components/menu/language/language-menu.component';
import {
  initializeLanguage,
  initializeSupportedLocales,
  initializeCasAuthEarly
} from './app.initializers';
import { UserMenuComponent } from './components/menu/user/user-menu.component';
import { ServiceListComponent } from './components/dashboard/service-list/service-list.component';
import { DocumentListComponent } from './components/dashboard/document-list/document-list.component';
import { MessageListComponent } from './components/dashboard/message-list/message-list.component';
import { TaskStatusComponent } from './components/task-status/task-status.component';
import { ModalComponent } from './components/modal/modal.component';
import { BusinessListComponent } from './components/business-list/business-list.component';
import { ManageBusinessEntityComponent } from './components/manage-business-entity/manage-business-entity.component';
import { ConfiguratorComponent } from './components/configurator/configurator.component';
import { ColorFieldComponent } from './components/configurator/components/color-field/color-field.component';
import { SwitchFieldComponent } from './components/configurator/components/switch-field/switch-field.component';
import { SelectFieldComponent } from './components/configurator/components/select-field/select-field.component';
import { InputFieldComponent } from './components/configurator/components/input-field/input-field.component';
import { ShadowFieldComponent } from './components/configurator/components/shadow-field/shadow-field.component';
import { KeepLatestUniqueInstanceWithNamePipe } from './keep-latest-unique-instance-with-name.pipe';
import { MonitoringControllerService } from './services/monitoring-controller/monitoring-controller.service';
import { WebsocketService } from './services/websocket.service';
import './CubanProductList';
import { CasService } from './services/auth/cas-service';
import { JwtModule } from '@auth0/angular-jwt';
import { Token } from './models/token';
import {
  persistKeycloakTokens,
  clearKeycloakTokens
} from './keycloak-token-sync';
import { BackendStatusBannerComponent } from './components/backend-status-banner/backend-status-banner.component';
import { ChangePasswordComponent } from './components/change-password/change-password.component';
import { StartedFilesInterceptor } from './started-files.interceptor';
import { AuthInterceptorService } from './auth-interceptor.service';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { MyAccountPageComponent } from './components/dashboard/my-account-page/my-account-page.component';
import { ColombiaIframeService } from './services/colombia-iframe.service';
import { DarkThemeService } from './services/dark-theme.service';
import { IfIframeDirective } from './directives/if-iframe-directive';
import { SharedModule } from './shared/shared.module';

// Normal import is not resolved
require('json-logic-extension');

/**
 * Registers event handlers for Keycloak instance
 */
function registerKeycloakHandlers(
  kcInstance: any,
  keycloak: KeycloakService
): void {
  kcInstance.onAuthSuccess = () => {
    persistKeycloakTokens(kcInstance);
  };
  kcInstance.onAuthRefreshSuccess = () => {
    persistKeycloakTokens(kcInstance);
  };
  kcInstance.onTokenExpired = () => {
    keycloak
      .updateToken(30)
      .then(() => persistKeycloakTokens(kcInstance))
      .catch(() =>
        keycloak.login({
          redirectUri: window.location.href
        })
      );
  };
  kcInstance.onAuthLogout = () => {
    clearKeycloakTokens();
  };

  // Persist immediately if tokens already present (e.g., after init)
  persistKeycloakTokens(kcInstance);
}

let AuthGuard;
const authProviders = [];
const authImports = [];

if (environment.KEYCLOAK_ACTIVE) {
  const initializeKeycloak = (keycloak: KeycloakService) => {
    return () => {
      // Save deep link URL before routing navigates away (FormComponent redirects to '/' on auth failure)
      try {
        const target =
          window.location.pathname +
          (window.location.search || '') +
          (window.location.hash || '');
        if (target !== '/') {
          sessionStorage.setItem(
            SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT,
            target
          );
        }
      } catch (e) {
        // sessionStorage may be unavailable in incognito/restricted contexts
      }

      // If instance already exists for some reason, register handlers now
      const preInitInstance: any = keycloak.getKeycloakInstance?.();
      if (preInitInstance) {
        try {
          registerKeycloakHandlers(preInitInstance, keycloak);
        } catch {
          // Intentionally ignore handler pre-init errors
        }
      }

      return keycloak
        .init({
          config: {
            url: environment.KEYCLOAK_URL,
            realm: environment.KEYCLOAK_REALM,
            clientId: environment.OAUTH_CLIENT_ID
          },
          initOptions: {
            // Disable the cross-tab session-sync iframe. Beyond losing that
            // (acceptable for DS — focused workflow, not a multi-tab dashboard),
            // this neutralizes a TOCTOU race inside keycloak-js 25.0.6:
            //   keycloak.mjs:631 sync guard `if (!kc.refreshToken)` passes,
            //   then `iframePromise.then(() => exec())` introduces an async
            //   gap, and at keycloak.mjs:651 `exec()` reads kc.refreshToken
            //   *again*. If concurrent code mutates it to undefined in the
            //   meantime, JS coerces undefined to the literal string
            //   "undefined" in `'refresh_token=' + kc.refreshToken`. Keycloak
            //   returns 400 invalid_grant; keycloak.mjs:685 then calls
            //   kc.clearToken(), silently logging the user out.
            // With checkLoginIframe disabled, loginIframe.enable is false, the
            // iframePromise branch is skipped, and exec() runs synchronously
            // after the guard — the value can't change between the two reads.
            // Pairs with the synchronous getUserRoles() change in
            // isUserInRole (companion PR) to eliminate this race source-side.
            checkLoginIframe: false,
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri:
              window.location.origin +
              (environment.LOCAL ? '' : '/parta') +
              '/assets/silent-check-sso.html'
          },
          bearerExcludedUrls: [
            '/parta',
            '/restheart',
            '/login',
            '/clients/public'
          ],
          bearerPrefix: 'Bearer'
        })
        .then((authenticated) => {
          if (authenticated) {
            // Already authenticated (SSO session exists) — clear saved deep link so it doesn't
            // misdirect a future login. The login() method only runs when unauthenticated, so
            // this cleanup never races with it.
            try {
              sessionStorage.removeItem(
                SESSION_STORAGE_KEYS.POST_LOGIN_REDIRECT
              );
            } catch (e) {
              // sessionStorage may be unavailable in incognito/restricted contexts
            }
          }
          const kcAfterInit: any = keycloak.getKeycloakInstance?.();
          if (kcAfterInit) {
            registerKeycloakHandlers(kcAfterInit, keycloak);
          }
          return authenticated;
        });
    };
  };

  AuthGuard = AuthGuardKeycloak;
  authProviders.push({
    provide: APP_INITIALIZER,
    useFactory: initializeKeycloak,
    multi: true,
    deps: [KeycloakService]
  });
  authImports.push(KeycloakAngularModule);
} else {
  AuthGuard = AuthGuardCAS;
  authProviders.push(CasService);
  authProviders.push(AuthGuard);
  authProviders.push({
    provide: APP_INITIALIZER,
    useFactory: initializeCasAuthEarly,
    deps: [],
    multi: true
  });
}

const jwtModuleAllowedDomains: string[] = [];

// A frontend may legitimately ship a disabled URL: the citizen front sets
// PARTC_URL/CAS_URL="null" on Keycloak-only instances (e.g. cuba), and any
// unset var is left as its "*_PLACEHOLDER" sentinel. Treat those as "nothing
// to allow-list" rather than an error — only a genuinely malformed URL is
// worth a log, and only when debugging (matching the old BASE_URL behaviour).
const addAllowedDomain = (name: string, value: string) => {
  if (!value || value === 'null' || value.endsWith('_PLACEHOLDER')) {
    return;
  }
  try {
    jwtModuleAllowedDomains.push(new URL(value).host);
  } catch (error) {
    if (environment.DEBUG_MODE) {
      console.log(`Error parsing ${name}`);
    }
  }
};

addAllowedDomain('BASE_URL', environment.BASE_URL);
addAllowedDomain('PARTC_URL', environment.PARTC_URL);
addAllowedDomain('KEYCLOAK_URL', environment.KEYCLOAK_URL);

/**
 * Factory function to configure ngx-bootstrap datepicker
 * Sets Monday as the first day of week (ISO 8601 standard)
 */
function getDatepickerConfig(): BsDatepickerConfig {
  return Object.assign(new BsDatepickerConfig(), {
    dateInputFormat: 'DD/MM/YYYY',
    // Use 'en-gb' locale which has Monday as first day of week
    containerClass: 'theme-default'
  });
}

@NgModule({
  declarations: [
    AppComponent,
    IfIframeDirective,
    HeaderComponent,
    LoginComponent,
    PageNameComponent,
    LanguageMenuComponent,
    MyAccountPageComponent,
    DashboardComponent,
    DashboardTabsComponent,
    ServiceListComponent,
    FileListComponent,
    DocumentPreviewComponent,
    DocumentListComponent,
    MessageListComponent,
    UserMenuComponent,
    TaskStatusComponent,
    ModalComponent,
    BusinessListComponent,
    ManageBusinessEntityComponent,
    ConfiguratorComponent,
    ColorFieldComponent,
    SwitchFieldComponent,
    SelectFieldComponent,
    InputFieldComponent,
    ShadowFieldComponent,
    KeepLatestUniqueInstanceWithNamePipe,
    BackendStatusBannerComponent,
    ChangePasswordComponent
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: APP_INITIALIZER,
      useFactory: () => initializeLanguage,
      multi: true
    },
    { provide: LOCALE_ID, useFactory: initializeSupportedLocales },
    AuthService,
    MonitoringControllerService,
    ...authProviders,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StartedFilesInterceptor,
      multi: true
    },
    BsDropdownDirective,
    { provide: FormioAppConfig, useValue: AppConfig },
    { provide: BsDatepickerConfig, useFactory: getDatepickerConfig },
    WebsocketService,
    ColombiaIframeService,
    DarkThemeService
  ],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    ...authImports,
    FormsModule,
    ReactiveFormsModule,
    NgxPaginationModule,
    NgSelectModule,
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    HttpClientModule,
    JwtModule.forRoot({
      // We cannot depend on DI Services here yet, so use local storage as a message bus :)
      config: {
        tokenGetter: () => {
          return new Promise<string | null>((resolve) => {
            // Needed, otherwise the local storage result might be null
            setTimeout(() => {
              const token = localStorage.getItem(Token.tokenNameInStorage);
              resolve(token);
            }, 100);
          });
        },
        allowedDomains: jwtModuleAllowedDomains,
        disallowedRoutes: [
          // `${environment.CAS_URL}/cback/v1.0/access_token`,
          // '/cback/v1.0/access_token'
        ],
        skipWhenExpired: true
      }
    }),
    FormioModule,
    TooltipModule.forRoot(),
    ModalModule.forRoot(),
    BsDropdownModule.forRoot(),
    ToastrModule.forRoot(),
    PopoverModule.forRoot(),
    SafePipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
